import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signupUser } from "../apis/auth";
import { toast } from "react-toastify";
import Lottie from "lottie-react";
import animationData from "../animation/signup.json";
import logo from "../assets/i2.png";
import { UserPlus, Shield, Eye, EyeOff, Mail, Lock, User, Phone, Sparkles, CheckCircle2 } from "lucide-react";

const Signup = () => {
  const [form, setForm] = useState({
    email: "",
    name: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const features = [
    "AI-powered WhatsApp automation",
    "Unlimited broadcast campaigns",
    "Advanced analytics dashboard",
    "24/7 customer support"
  ];

  useEffect(() => {
    document.title = "Blutec PING Signup - Create Your Free WhatsApp Messaging Account";
    const description =
      "Sign up for Blutec PING and start free WhatsApp broadcasting, bulk messaging, and chatbot automation. Quick setup—get instant access to smart business tools.";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", description);
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      toast.error("Passwords do not match.");
      return;
    }
    setIsLoading(true);
    try {
      await signupUser({ ...form, confirmPassword: undefined });
      toast.success("Signup successful! Redirecting to login...");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      const message = err.response?.data?.message || "Signup failed.";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen w-screen overflow-hidden bg-white flex">
      {/* Left Side - Decorative */}
      <div className="hidden lg:flex lg:w-1/2 h-full relative overflow-hidden" style={{ backgroundColor: "#e9eeea" }}>
        <div className="absolute inset-0 flex flex-col items-center justify-center p-8">
          {/* Decorative circles */}
          <div className="absolute top-20 left-20 w-64 h-64 rounded-full opacity-20" style={{ backgroundColor: "#17181b" }} />
          <div className="absolute bottom-20 right-20 w-96 h-96 rounded-full opacity-10" style={{ backgroundColor: "#17181b" }} />
          
          <div className="relative z-10 text-center max-w-lg">
            <div className="mb-6 inline-flex items-center gap-3 px-6 py-3 rounded-2xl shadow-lg" style={{ backgroundColor: "#EEFA94" }}>
              <div className="p-2 bg-[#17181b]/90 rounded-xl">
                <Sparkles className="text-white" size={24} />
              </div>
              <span className="text-3xl font-bold text-gray-900">BluTec PING</span>
            </div>
            
            <h1 className="text-4xl font-extrabold text-gray-900 mb-4 leading-tight">
              Start Your Journey Today
            </h1>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Join thousands of businesses using BluTec PING to scale their WhatsApp communication
            </p>

            {/* Features List */}
            <div className="bg-white rounded-2xl p-5 border border-gray-200 text-left shadow-sm">
              <h3 className="text-base font-bold text-gray-900 mb-3">What you'll get:</h3>
              <div className="space-y-2.5">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="p-1.5 bg-[#17181b]/90 rounded-lg">
                      <CheckCircle2 className="text-white flex-shrink-0" size={16} />
                    </div>
                    <span className="text-sm text-gray-700 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Signup Form */}
      <div className="w-full lg:w-1/2 h-full flex items-center justify-center p-4 sm:p-8 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-6">
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-2xl shadow-lg mb-4" style={{ backgroundColor: "#EEFA94" }}>
              <div className="p-2 bg-[#17181b]/90 rounded-xl">
                <Sparkles className="text-white" size={20} />
              </div>
              <span className="text-2xl font-bold text-gray-900">BluTec PING</span>
            </div>
          </div>

          {/* Header */}
          <div className="mb-5">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-2">
              Create Account
            </h2>
            <p className="text-gray-600">
              Get started with your free account
            </p>
          </div>

          {/* Security Badge */}
          <div className="flex justify-center mb-5">
            <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm">
              <div className="p-1 bg-[#17181b]/90 rounded-lg">
                <Shield size={14} className="text-white" />
              </div>
              Secure Registration
            </span>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-5 rounded-2xl border border-red-300 bg-red-50 p-4">
              <div className="flex items-start gap-3">
                <div className="text-red-600 text-sm font-semibold">{error}</div>
              </div>
            </div>
          )}

          {/* Signup Form */}
          <form onSubmit={handleSubmit} className="space-y-3.5">
            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Enter your full name"
                  autoComplete="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-gray-200 bg-white pl-12 pr-4 py-3 text-base text-gray-900 placeholder-gray-400 shadow-sm outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-300 transition"
                />
              </div>
            </div>

            {/* Phone Field */}
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                Phone Number
              </label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="Enter your phone number"
                  autoComplete="tel"
                  value={form.phone}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-gray-200 bg-white pl-12 pr-4 py-3 text-base text-gray-900 placeholder-gray-400 shadow-sm outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-300 transition"
                />
              </div>
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@company.com"
                  autoComplete="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-gray-200 bg-white pl-12 pr-4 py-3 text-base text-gray-900 placeholder-gray-400 shadow-sm outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-300 transition"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create password"
                  value={form.password}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-gray-200 bg-white pl-12 pr-12 py-3 text-base text-gray-900 placeholder-gray-400 shadow-sm outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-300 transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm password"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-gray-200 bg-white pl-12 pr-12 py-3 text-base text-gray-900 placeholder-gray-400 shadow-sm outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-300 transition"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-xl py-3.5 text-base font-semibold text-white bg-[#17181b]/90 shadow-md hover:shadow-lg hover:bg-[#17181b]/80 transition-all focus:outline-none focus:ring-2 focus:ring-[#17181b]/30 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-3">
                  <div className="w-5 h-5 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
                  <span>Creating Account...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <UserPlus size={18} />
                  <span>Create Account</span>
                </div>
              )}
            </button>
          </form>

          {/* Terms */}
          <p className="text-xs text-gray-500 text-center mt-4">
            By creating an account, you agree to our{" "}
            <a href="/terms" className="text-gray-700 hover:text-gray-900 font-semibold transition">
              Terms & Conditions
            </a>
          </p>

          {/* Sign In Link */}
          <div className="mt-5 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{" "}
              <a
                href="/login"
                className="font-semibold text-gray-900 hover:text-gray-700 transition"
              >
                Sign in
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
